import {
  Badge,
  Button,
  cn,
  DrawerOrDialog,
  Input,
  useIsGummy,
  useMediaQuery
} from "@swivu/shared-ui";
import {
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from "@heroicons/react/24/outline/index.js";
import { forwardRef, useRef, useState, useEffect } from "react";
import type { ButtonHTMLAttributes } from "react";

export type EntityTone =
  | "files"
  | "contacts"
  | "checklists"
  | "reminders"
  | "notes"
  | "neutral";

type CommonStylesProps = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>> | typeof CalendarDaysIcon;
  title: string;
  cardSubtitle?: string;
  cardTitle?: string;
  badge?: number;
  tone?: EntityTone;
  children: React.ReactNode;
  wrapperClassName?: string;
  onOpenChange?: (open: boolean) => void;
  search?: {
    filter: string;
    clearSearch: () => void;
    onSearch: (search: string) => void;
  };
  contentClassNames?: string;
  actions?: React.ReactNode;
};

const toneGradient: Record<EntityTone, string> = {
  files: "bg-tile-media text-tile-media-text",
  contacts: "bg-tile-people text-tile-people-text",
  checklists: "bg-tile-success text-tile-success-text",
  reminders: "bg-tile-alert text-tile-alert-text",
  notes: "bg-tile-schedule text-tile-schedule-text",
  neutral: "bg-tile-muted text-tile-muted-text"
};

type GummyEntityTileProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  tone: EntityTone;
};

const GummyEntityTile = forwardRef<HTMLButtonElement, GummyEntityTileProps>(
  ({ Icon, title, tone, className, style, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        "shadow-raised",
        "relative flex aspect-square w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-surface-tile px-4 py-5 text-white",
        toneGradient[tone],
        "transition-transform duration-150",
        "hover:-translate-y-0.5 active:scale-[0.96]",
        "motion-reduce:transform-none motion-reduce:transition-none",
        "outline-none focus-visible:ring-4 focus-visible:ring-white/80",
        className
      )}
      style={{
        textShadow: "var(--text-shadow-on-color)",
        ...style
      }}
      {...rest}
    >
      <span aria-hidden className={"top-gloss-large"} />
      <Icon className="relative size-16 flex-none" aria-hidden />
      <span className="relative text-center text-2xl font-black leading-[1.15] tracking-tight">
        {title}
      </span>
    </button>
  )
);
GummyEntityTile.displayName = "GummyEntityTile";

const CommonStyles = ({
  Icon,
  title,
  children,
  badge,
  cardTitle,
  wrapperClassName,
  search,
  onOpenChange,
  contentClassNames,
  actions,
  cardSubtitle,
  tone = "neutral"
}: CommonStylesProps) => {
  const initialHeight = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const isGummy = useIsGummy();

  useEffect(() => {
    if (isOpen && initialHeight.current) {
      setTimeout(() => {
        setHeight(initialHeight.current?.offsetHeight);
      }, 100);
    }
  }, [isOpen]);
  // return null;
  return (
    <DrawerOrDialog.Root
      dialog={{
        onOpenChange(open) {
          if (open) {
            setHeight(undefined);
          }
          setIsOpen(open);
        }
      }}
      drawer={{
        modal: true,
        onOpenChange(open) {
          if (onOpenChange) onOpenChange(open);
        }
      }}
    >
      <div className="relative">
        <DrawerOrDialog.Trigger asChild>
          {isGummy ? (
            <GummyEntityTile Icon={Icon} title={title} tone={tone} />
          ) : (
            <Button
              className={`mobile-pref-control flex h-full w-full items-center justify-center text-sm`}
              action="secondary"
            >
              <div
                className={`flex w-full items-center justify-center gap-x-4 text-center md:gap-x-2`}
              >
                <Icon
                  className={`mobile-pref-detail-inline-icon text-mono-500 dark:text-mono-200`}
                />
                <h4
                  className={`mobile-pref-card-title text-center leading-5 md:text-sm`}
                >
                  {title}
                </h4>
              </div>
            </Button>
          )}
        </DrawerOrDialog.Trigger>
        {isGummy && badge ? (
          <Badge
            count={badge}
            className="left-auto -top-3 -right-3 h-12 min-w-12 px-3 text-lg font-black pointer-events-none bg-white text-mono-900 border-0 shadow-raised dark:bg-mono-900 dark:text-white dark:border-mono-950"
          />
        ) : null}
        {!isGummy && badge && (
          <Badge
            count={badge}
            className={`mobile-pref-detail-badge pointer-events-none border-0 bg-card p-2 text-mono-800 border-mono-200 dark:text-mono-200 dark:border-mono-700/50 -translate-x-4 -translate-y-2`}
          />
        )}
      </div>
      <DrawerOrDialog.Content
        position="top"
        ref={initialHeight}
        innerClassName={` `}
        className={cn(
          `dark:text-mono-200 dark:bg-mono-950/80 md:max-w-(--breakpoint-md) bg-mono-100  md:flex md:flex-col backdrop-blur-xl`,
          contentClassNames
        )}
        style={{
          height: height && isDesktop ? `${height}px` : undefined
        }}
      >
        <div
          className={cn(
            `flex flex-1 flex-col overflow-hidden`,
            isGummy && `pt-2`
          )}
        >
          <DrawerOrDialog.Title
            className={cn(
              `grid justify-between gap-4 px-4 pb-4 pt-4 md:grid-cols-[1fr_auto] md:px-0 md:pt-0 md:pr-6`,
              !isGummy && `dark:border-mono-700/50 border-b`,
              `transition-[grid-template-columns] duration-300 ease-in-out items-start`,
              isMobileSearchOpen && search
                ? `grid-cols-[0fr_1fr] md:grid-cols-[1fr_auto]`
                : `grid-cols-[1fr_auto]`
            )}
          >
            {/* Title content - collapses on mobile when search is open */}
            <div
              className={cn(
                `relative flex min-w-0 justify-between gap-x-3 gap-y-4 overflow-hidden transition-opacity duration-300`,
                isMobileSearchOpen && search
                  ? `opacity-0 md:opacity-100`
                  : `opacity-100`
              )}
            >
              <div
                className={`flex flex-1 gap-x-3 ${
                  cardSubtitle ? " items-start " : " items-center "
                }`}
              >
                <div
                  className={cn(
                    "",
                    isGummy
                      ? cn(
                          "shadow-sunken p-2.5",
                          "rounded-surface-sm",
                          toneGradient[tone]
                        )
                      : "dark:bg-mono-900 text-mono-500 dark:text-mono-100 rounded-md bg-white"
                  )}
                >
                  <Icon className={cn(" flex-none", isGummy && "size-12")} />
                </div>
                <div className="min-w-0">
                  <h4
                    className={cn(
                      " flex-1 line-clamp-2 text-left text-xl",
                      isGummy
                        ? "font-black tracking-tight text-mono-900 dark:text-white leading-6"
                        : "  font-normal dark:text-white"
                    )}
                  >
                    <p>{cardTitle || title}</p>
                  </h4>
                  {cardSubtitle && (
                    <p
                      className={cn(
                        "mobile-pref-card-meta line-clamp--2 leading-tight",
                        isGummy
                          ? "text-sm font-black uppercase tracking-widest text-mono-600 dark:text-mono-300 mt-2"
                          : "text-muted-foreground text-xs md:text-sm"
                      )}
                    >
                      {cardSubtitle}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/* Actions - search button on mobile, full search on desktop */}
            <div className="flex items-center justify-end  gap-2">
              {search && (
                <>
                  {/* Mobile: animated search input */}
                  <div
                    className={cn(
                      `grid transition-[grid-template-columns] duration-300 ease-in-out md:hidden`,
                      isMobileSearchOpen
                        ? `grid-cols-[1fr] flex-1 `
                        : `grid-cols-[0fr]`
                    )}
                  >
                    <div className="min-w-0 overflow-hidden">
                      <SearchInputMobile
                        {...search}
                        isSearchOpen={isMobileSearchOpen}
                        setIsSearchOpen={setIsMobileSearchOpen}
                        isGummy={isGummy}
                      />
                    </div>
                  </div>
                  {/* Mobile: search toggle button */}
                  {!isMobileSearchOpen &&
                    (isGummy ? (
                      <button
                        type="button"
                        aria-label="Search files"
                        onClick={() => setIsMobileSearchOpen(true)}
                        className={cn(
                          "mobile-pref-control-tight h-14 w-14 md:hidden inline-flex items-center justify-center",
                          "rounded-surface-sm bg-tile-neutral text-mono-900",
                          "shadow-raised",
                          "dark:from-mono-800 dark:to-mono-900 dark:text-mono-100",
                          "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
                          "motion-reduce:transform-none motion-reduce:transition-none",
                          "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40"
                        )}
                      >
                        <MagnifyingGlassIcon
                          className="mobile-pref-control-icon mobile-pref-detail-inline-icon size-7"
                          strokeWidth={3}
                        />
                      </button>
                    ) : (
                      <Button
                        action="secondary"
                        aria-label="Search files"
                        className="mobile-pref-control-tight h-9 md:hidden"
                        onClick={() => setIsMobileSearchOpen(true)}
                      >
                        <MagnifyingGlassIcon className="mobile-pref-control-icon mobile-pref-detail-inline-icon" />
                      </Button>
                    ))}
                  {/* Desktop: always visible search */}
                  <div className="hidden flex-none md:block px-4">
                    <SearchInputDesktop {...search} isGummy={isGummy} />
                  </div>
                </>
              )}
              {actions}
            </div>
          </DrawerOrDialog.Title>
          <div
            className={cn(
              `flex min-h-0 flex-1 overflow-scroll px-4 pb-6 md:pb-2`,
              isGummy ? `md:px-3 pt-2 pb-8 md:pb-4` : `md:px-0`,
              wrapperClassName
            )}
          >
            {children}
          </div>
        </div>
      </DrawerOrDialog.Content>
    </DrawerOrDialog.Root>
  );
};

const GUMMY_SEARCH_INPUT = cn(
  "w-full rounded-surface-sm border-0 bg-white dark:bg-mono-900 text-mono-900 dark:text-mono-100",
  "shadow-sunken",
  "placeholder:text-mono-400 dark:placeholder:text-mono-500",
  "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40",
  "transition-[box-shadow]"
);

const SearchInputDesktop = ({
  filter,
  onSearch,
  clearSearch,
  isGummy
}: {
  filter: string;
  onSearch: (search: string) => void;
  clearSearch: () => void;
  isGummy?: boolean;
}) => {
  return (
    <div className={`group relative w-64`}>
      {isGummy ? (
        <input
          value={filter}
          type="text"
          placeholder="Search..."
          onChange={(e) => onSearch(e.target.value)}
          className={cn(
            "mobile-pref-command-input py-3 pl-5 pr-10 h-14 text-base font-black placeholder:font-bold",
            GUMMY_SEARCH_INPUT
          )}
        />
      ) : (
        <Input
          value={filter}
          type="text"
          placeholder="Search files..."
          onChange={(e) => onSearch(e.target.value)}
          className="mobile-pref-command-input border-mono-200 dark:border-mono-700 dark:bg-mono-950 w-full rounded-md border bg-white py-2 pl-4 pr-3"
        />
      )}
      {filter?.length ? (
        <button
          onClick={() => clearSearch()}
          className={`absolute right-0 top-1/2 mr-3 -translate-y-1/2 opacity-70 transition-opacity focus:opacity-100 group-hover:opacity-100`}
        >
          <XMarkIcon
            className={cn(
              "mobile-pref-control-icon mobile-pref-detail-icon",
              isGummy && "size-6"
            )}
            strokeWidth={3}
          />
        </button>
      ) : (
        <MagnifyingGlassIcon
          className={cn(
            "mobile-pref-control-icon mobile-pref-detail-icon absolute right-0 top-1/2 mr-3 -translate-y-1/2 opacity-50 transition-opacity focus:opacity-100 group-hover:opacity-100",
            isGummy && "size-6"
          )}
          strokeWidth={3}
        />
      )}
    </div>
  );
};

const SearchInputMobile = ({
  filter,
  onSearch,
  clearSearch,
  isSearchOpen,
  setIsSearchOpen,
  isGummy
}: {
  filter: string;
  onSearch: (search: string) => void;
  clearSearch: () => void;
  isSearchOpen: boolean;
  setIsSearchOpen: (open: boolean) => void;
  isGummy?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSearchOpen) {
      inputRef.current?.focus();
    }
  }, [isSearchOpen]);

  return (
    <div className="group relative flex items-center">
      {isGummy ? (
        <input
          ref={inputRef}
          value={filter}
          type="text"
          placeholder="Search..."
          onChange={(e) => onSearch(e.target.value)}
          className={cn(
            "mobile-pref-command-input py-3 pl-5 pr-12 h-14 text-base font-black placeholder:font-bold",
            GUMMY_SEARCH_INPUT
          )}
        />
      ) : (
        <Input
          ref={inputRef}
          value={filter}
          type="text"
          placeholder="Search files..."
          onChange={(e) => onSearch(e.target.value)}
          className="mobile-pref-command-input border-mono-200 dark:border-mono-700 dark:bg-mono-950 w-full rounded-md border bg-white py-2 pl-4 pr-10"
        />
      )}
      <button
        onClick={() => {
          clearSearch();
          setIsSearchOpen(false);
        }}
        className="absolute right-3 top-1/2 -translate-y-1/2"
      >
        <XMarkIcon
          className={cn(
            "mobile-pref-control-icon mobile-pref-detail-inline-icon opacity-70",
            isGummy && "size-7"
          )}
          strokeWidth={3}
        />
      </button>
    </div>
  );
};
export default CommonStyles;
export { GummyEntityTile };
